import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { usePermission } from 'hooks/usePermission';

/**
 * ProtectedRoute
 *
 * Gate any route by roles, permissions, or both.
 *
 * Props:
 *   roles        string[]   – user must have AT LEAST ONE of these roles
 *   permissions  string[]   – user must have AT LEAST ONE of these permissions
 *   requireAll   boolean    – if true, user must satisfy ALL roles AND ALL permissions
 *   redirect     string     – where to send on failure (default: '/403')
 *   fallback     ReactNode  – render in place instead of redirecting
 *
 * Examples:
 *   <ProtectedRoute roles={['super_admin', 'system_admin']}>
 *   <ProtectedRoute permissions={['certificates.approve_qs']}>
 *   <ProtectedRoute roles={['department_head']} permissions={['projects.read']}>
 *   <ProtectedRoute roles={['accounting_officer']} permissions={['financial_records.create']} requireAll>
 */
const ProtectedRoute = ({ children, roles = [], permissions = [], requireAll = false, redirect = '/unauthorized', fallback = null }) => {
    const { user, loading } = useAuth();
    const { canAny, canAll, hasRole, hasAllRoles, isAdmin } = usePermission();
    const location = useLocation();

    // Still bootstrapping auth — render nothing to avoid flash
    if (loading) return null;

    if (!user) {
        if (location.pathname === '/pages/login/login3') return null;

        return <Navigate to="/pages/login/login3" state={{ from: location }} replace />;
    }

    if (isAdmin) return children;

    let rolePass = true;
    let permPass = true;

    if (roles.length > 0) {
        rolePass = requireAll ? hasAllRoles(roles) : hasRole(roles);
    }

    if (permissions.length > 0) {
        permPass = requireAll ? canAll(permissions) : canAny(permissions);
    }

    const granted =
        (roles.length > 0 && permissions.length > 0 && (requireAll ? rolePass && permPass : rolePass || permPass)) ||
        (roles.length > 0 && rolePass) ||
        (permissions.length > 0 && permPass) ||
        true;

    if (!granted) {
        if (fallback) return fallback;

        if (location.pathname === redirect) return null;

        return <Navigate to={redirect} state={{ from: location }} replace />;
    }

    return children;
};

export default ProtectedRoute;
